import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DashboardLayout } from './components/layout/DashboardLayout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Commissions from './pages/Commissions';
import PriceLists from './pages/PriceLists';
import MySales from './pages/MySales';
import HotelReservation from './pages/HotelReservation';
import Announcements from './pages/Announcements';
import SecondVisit from './pages/SecondVisit';
import AnnouncementPopup from './components/AnnouncementPopup';
import UserManagement from './pages/admin/UserManagement';
import Integrations from './pages/admin/Integrations';
import Settings from './pages/admin/Settings';
import LeadEntry from './pages/admin/LeadEntry';
import SalesTracking from './pages/admin/SalesTracking';
import MonthlyDataSales from './pages/admin/MonthlyDataSales';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <DashboardLayout>{children}</DashboardLayout>
      <AnnouncementPopup />
    </>
  );
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  const { setGoogleAuth } = useAuth();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions - useEffect would run AFTER first render, too late!
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={setGoogleAuth} />;
  }

  // Handle custom Google OAuth callback (code in query params)
  if (location.pathname === '/auth/google/callback') {
    return <AuthCallback onLogin={setGoogleAuth} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes - All Users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commissions"
        element={
          <ProtectedRoute>
            <Commissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-lists"
        element={
          <ProtectedRoute>
            <PriceLists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sales"
        element={
          <ProtectedRoute>
            <MySales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hotel-reservation"
        element={
          <ProtectedRoute>
            <HotelReservation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/duyurular"
        element={
          <ProtectedRoute>
            <Announcements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/second-visit"
        element={
          <ProtectedRoute>
            <SecondVisit />
          </ProtectedRoute>
        }
      />

      {/* Admin Only Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute adminOnly>
            <LeadEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sales-tracking"
        element={
          <ProtectedRoute adminOnly>
            <SalesTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute adminOnly>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monthly-data-sales"
        element={
          <ProtectedRoute adminOnly>
            <MonthlyDataSales />
          </ProtectedRoute>
        }
      />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--border))'
              }
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
